import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import { Purchases } from '@revenuecat/purchases-js';
import { useSelector, useDispatch } from 'react-redux';
import { getProfile, createUser, updateLanguage as updateLanguageAction } from '../redux/slices/user';
import { resetOnboarding } from '../redux/slices/app';
import { firebaseConfig } from '../config';
import axios from '../utils/axios';
import i18n from '../locales/i18n';
import { consumeStoredReferralCode, clearStoredReferralCode } from '../utils/referralCapture';

// ----------------------------------------------------------------------

 if (process.env.NODE_ENV !== 'production') {
  if (!firebase.apps.length) {
    firebase.initializeApp(firebaseConfig);
  }
  firebase.auth().useEmulator('http://localhost:9099');
} else if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}


const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const reducer = (state, action) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: 'firebase',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  loginWithApple: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  updateLanguage: () => Promise.resolve(),
  updateProfile: () => Promise.resolve(),
  signInAnonymously: () => Promise.resolve(),
  linkAnonymousAccount: () => Promise.resolve(),
  linkAnonymousAccountWithGoogle: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  // const [profile, setProfile] = useState(null);
  const { profile } = useSelector((state)=>state.user)
  const [state, dispatch] = useReducer(reducer, initialState);
  const reduxDispatch = useDispatch()

  useEffect(
    () =>
      firebase.auth().onIdTokenChanged(async (user) => {
        if (user && !user.isAnonymous) {
          // A valid non-anonymous Firebase user is the actual authentication signal — claims and
          // RevenueCat are best-effort enrichment and must never be able to strand a genuinely
          // logged-in user (e.g. one who just registered) on isAuthenticated: false if either
          // throws. Each is isolated so one failing doesn't skip the other.
          let claims;
          try {
            ({ claims } = await user.getIdTokenResult());
          } catch (error) {
            console.error('Failed to read ID token claims:', error);
          }

          reduxDispatch(getProfile(user.uid));

          try {
            // Initialize or switch RevenueCat to this user's identity
            const rcKey = process.env.REACT_APP_REVENUECAT_PUBLIC_KEY;
            if (rcKey) {
              if (!Purchases.isConfigured()) {
                Purchases.configure({ apiKey: rcKey, appUserId: user.uid });
              } else {
                Purchases.getSharedInstance().changeUser(user.uid);
              }
            }
          } catch (error) {
            console.error('RevenueCat identity sync failed:', error);
          }

          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: true, user: {...user, role: claims?.role} }
          });
        } else {
          if (Purchases.isConfigured()) {
            Purchases.getSharedInstance().close();
          }
          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: false, user: null }
          });
        }
      }),
    [dispatch, reduxDispatch]
  );

  // Backend profile is the source of truth for a logged-in user's language across devices —
  // sync it into i18next once the profile loads, but only if it actually differs so we don't
  // clobber a change the user just made locally in this same session.
  useEffect(() => {
    if (profile?.language && profile.language !== i18n.language) {
      i18n.changeLanguage(profile.language);
    }
  }, [profile?.language]);

  // Fire-and-forget: a failed attribution shouldn't block signup itself, same tolerance this
  // file already gives the nearby RevenueCat configure/changeUser calls. Called once right after
  // each of the two signup-completion paths below succeeds.
  const attributeReferralIfPending = () => {
    const code = consumeStoredReferralCode();
    if (!code) return;
    axios
      .post(`${process.env.REACT_APP_HOST_API}/profiles/attribute-referral`, { code })
      .then(() => clearStoredReferralCode())
      .catch((error) => console.error(error));
  };

  const login = (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);

  const loginWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithApple = () => {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return firebase.auth().signInWithPopup(provider);
  };

  const register = async (email, password, firstName, lastName) => {
    await reduxDispatch(createUser({ email, password, firstName, lastName }));
    const result = await firebase.auth().signInWithEmailAndPassword(email, password);
    attributeReferralIfPending();
    return result;
  };

  // Onboarding-only: gives a fresh visitor a real (if anonymous) Firebase uid/token so the
  // upload step can call the ingestion API before an account exists — see
  // docs/superpowers/specs/2026-07-24-snacky-onboarding-design.md Section D. Not used by the
  // standalone /auth/register or /auth/login pages.
  const signInAnonymously = () => firebase.auth().signInAnonymously();

  // Upgrades the current anonymous session to a permanent one **in place** (same uid), instead
  // of minting a brand-new Firebase user the way `register` above does — this is what keeps a
  // deck built during onboarding's upload step attached to the account the visitor ends up
  // with. Only ever called from OnboardingAuthGate's register path.
  const linkAnonymousAccount = async (email, password) => {
    const credential = firebase.auth.EmailAuthProvider.credential(email, password);
    const result = await firebase.auth().currentUser.linkWithCredential(credential);
    attributeReferralIfPending();
    return result;
  };

  const linkAnonymousAccountWithGoogle = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    const result = await firebase.auth().currentUser.linkWithPopup(provider);
    attributeReferralIfPending();
    return result;
  };

  // isOnboarded/answers (redux/slices/app.js) are persisted to localStorage keyed to the browser,
  // not the account — on a shared/reused browser, a previous account's completed-onboarding flag
  // would otherwise leak into the next account's session and skip AuthGuard/GuestGuard's redirect
  // to /onboarding for what is, for them, a first-time visit.
  const logout = async () => {
    await firebase.auth().signOut();
    reduxDispatch(resetOnboarding());
  };

  const resetPassword = async (email) => {
    await axios.get(`${process.env.REACT_APP_HOST_API}/users/forgotten-password`, { params: { email } });
  };

  const updateLanguage = async (language) => {
    await reduxDispatch(updateLanguageAction(language));
  };

  // Updates the Firebase Auth user's own displayName/photoURL — the primary source for both
  // (see the `auth.photoURL || profile?.photoURL` fallback below, `auth` always wins when set).
  // onIdTokenChanged does not re-fire for a profile-only update, so local state is patched
  // directly afterwards so every consumer (avatar in the header/bottom-nav/Profile page, this
  // same form) reflects the change immediately instead of waiting for the next token refresh.
  const updateProfile = async ({ displayName, photoURL }) => {
    await firebase.auth().currentUser.updateProfile({ displayName, photoURL });
    dispatch({
      type: 'INITIALISE',
      payload: { isAuthenticated: true, user: { ...state.user, displayName, photoURL } },
    });
  };

  const auth = { ...state.user };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user: {
          id: auth.uid,
          email: auth.email,
          photoURL: auth.photoURL || profile?.photoURL,
          displayName: auth.displayName || profile?.displayName,
          idToken: auth.idToken,
          role: auth.role,
          phoneNumber: auth.phoneNumber || profile?.phoneNumber || '',
          about: profile?.about || '',
          isPublic: profile?.isPublic || false,
          membership: profile?.subscriptionTier || null,
          xp: profile?.xp || 0,
          language: profile?.language || 'en',
          notifications: profile?.notifications || {
            practiceReminder: false,
            reminderTime: '09:00',
            reminderTimezone: 'UTC',
          },
        },
        login,
        register,
        loginWithGoogle,
        loginWithApple,
        logout,
        resetPassword,
        updateLanguage,
        updateProfile,
        signInAnonymously,
        linkAnonymousAccount,
        linkAnonymousAccountWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
